import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { ContentEditEntry } from '../../types';

interface Props {
  domainId: string;
}

// Friendly label map for known field paths
const FIELD_LABELS: Record<string, string> = {
  'schema.pageSignals.pageTitle':     'Meta title',
  'schema.pageSignals.metaDesc':      'Meta description',
  'schema.pageSignals.ogTitle':       'OG title',
  'schema.pageSignals.ogDescription': 'OG description',
  'schema.businessName':              'Business name',
  'schema.category':                  'Category',
  'schema.description':               'Description',
  'schema.address.streetAddress':     'Street address',
  'schema.address.addressLocality':   'City',
  'schema.address.postalCode':        'Postcode',
  'schema.address.addressCountry':    'Country',
  'schema.telephone':                 'Phone',
  'schema.sameAsUrls':                'sameAs URLs',
  'faqPairs':                         'FAQ pairs',
};

function fieldLabel(path: string): string {
  if (FIELD_LABELS[path]) return FIELD_LABELS[path];
  // Fallback: use last segment, title-cased
  const last = path.split('.').pop() ?? path;
  return last.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function truncate(value: string, max = 80): string {
  if (!value) return '—';
  return value.length > max ? value.slice(0, max) + '…' : value;
}

interface EditRow {
  path: string;
  entry: ContentEditEntry;
}

export function ActivityTab({ domainId }: Props) {
  const [edits, setEdits] = useState<EditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.domains.getContent(domainId)
      .then((res) => {
        const rows: EditRow[] = Object.entries(res.edits)
          .map(([path, entry]) => ({ path, entry }))
          .sort((a, b) => {
            const ta = a.entry.updated_at ? new Date(a.entry.updated_at).getTime() : 0;
            const tb = b.entry.updated_at ? new Date(b.entry.updated_at).getTime() : 0;
            return tb - ta;
          });
        setEdits(rows);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load activity'))
      .finally(() => setLoading(false));
  }, [domainId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) return <p className="text-sm text-red-500">{error}</p>;

  if (edits.length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-8 text-center">
        <p className="text-gray-500 text-sm mb-1">No edits yet</p>
        <p className="text-xs text-gray-400">
          Changes you make in the Content tab will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">
        {edits.length} field{edits.length !== 1 ? 's' : ''} edited
      </p>
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
        {edits.map(({ path, entry }, i) => (
          <div
            key={path}
            className={`px-5 py-4 ${i < edits.length - 1 ? 'border-b border-gray-50' : ''}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-brand-ink">{fieldLabel(path)}</span>
                  <span className="text-xs bg-green-50 text-brand-green px-2 py-0.5 rounded-full">Edited</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed truncate">
                  {truncate(entry.human_value)}
                </p>
                {entry.ai_value && entry.ai_value !== entry.human_value && (
                  <p className="text-xs text-gray-400 mt-1 italic">
                    AI original: {truncate(entry.ai_value, 60)}
                  </p>
                )}
              </div>
              {entry.updated_at && (
                <span className="text-xs text-gray-400 shrink-0 mt-0.5">
                  {timeAgo(entry.updated_at)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
